import { Trip } from "@fairfleet/geotab";
import { useMemo } from "react";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { historicalStatusDataQuery } from "@/hooks/statusData/useHistoricalStatusData";
import { usePerDeviceQueries } from "@/hooks/usePerDeviceQueries";
import { QueryDeviceData, QueryStatusData } from "@/models/QueryDiagnosticDeviceData";
import { fetchTripsComplete } from "@/utilities/query/fetchTripsComplete";

// Only ids that exist in Geotab's KnownId list. GPS speed lives on LogRecord, not StatusData.
export const DIAGNOSTIC_SPEED_ID = "DiagnosticEngineRoadSpeedId";
export const DIAGNOSTIC_FUEL_USED_ID = "DiagnosticTotalTripFuelUsedId";

export const FLEET_VEHICLE_QUERY_KEY = "fleetDashboardVehicle";

/**
 * Vehicles loaded at once. Each costs 3 calls (2 StatusData series, 1 Trip search), so this many
 * fill about one client queue flush (`queueMaxSize` 100): fewer would wait out the 1.5 s queue
 * timer more often, all at once would queue every call of the fleet in one go.
 */
export const FLEET_VEHICLE_CONCURRENCY = 33;

interface VehicleFigures {
  speed: QueryStatusData[];
  fuel: QueryStatusData[];
  trips: Trip[];
}

/**
 * Speed and fuel readings plus trips of every vehicle, one query per vehicle so the page can
 * show how many vehicles are done and a failing vehicle does not discard the others.
 */
export function useFleetVehicleFigures(
  deviceIds: string[],
  fromDate: Date,
  toDate: Date,
  enabled: boolean,
) {
  const { geotabClient, activeSessionId } = useGeotabClient();

  const vehicles = usePerDeviceQueries<VehicleFigures>({
    queryKey: FLEET_VEHICLE_QUERY_KEY,
    deviceIds,
    fromDate,
    toDate,
    enabled,
    maxConcurrent: FLEET_VEHICLE_CONCURRENCY,
    // A single record per vehicle; usePerDeviceQueries collects record lists.
    fetchRecords: async (deviceId, signal) => {
      const statusQuery = historicalStatusDataQuery(
        geotabClient,
        activeSessionId,
        [DIAGNOSTIC_SPEED_ID, DIAGNOSTIC_FUEL_USED_ID],
        [deviceId],
        fromDate,
        toDate,
        false,
      );
      const [status, trips] = await Promise.all([
        statusQuery.queryFn({ signal }),
        fetchTripsComplete(
          (search, resultsLimit) =>
            geotabClient.get("Trip", search, resultsLimit, signal) as Promise<Trip[]>,
          { fromDate, toDate, searches: [{ deviceSearch: { id: deviceId } }], signal },
        ),
      ]);
      return [
        {
          speed: status[DIAGNOSTIC_SPEED_ID][deviceId],
          fuel: status[DIAGNOSTIC_FUEL_USED_ID][deviceId],
          trips,
        },
      ];
    },
  });

  const { byDevice } = vehicles;
  const figures = useMemo(() => {
    const speedData: QueryDeviceData = {};
    const fuelData: QueryDeviceData = {};
    const trips: Trip[] = [];
    for (const [deviceId, [vehicle]] of Object.entries(byDevice)) {
      speedData[deviceId] = vehicle.speed;
      fuelData[deviceId] = vehicle.fuel;
      trips.push(...vehicle.trips);
    }
    return { speedData, fuelData, trips };
  }, [byDevice]);

  return {
    ...figures,
    loadedCount: vehicles.loadedCount,
    failedCount: vehicles.failedCount,
    totalCount: vehicles.totalCount,
    isLoading: vehicles.isLoading,
    isRetrying: vehicles.isRetrying,
    retryFailed: vehicles.retryFailed,
  };
}
